import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { BrandMark } from '../ui/Brand';
import { isStaffRole } from '../../constants/botNav';

/**
 * A bot oldalának fejléce (bot.mortisengine.com) — a Codex fejlécének mása (60 px, ragadós, áttetsző alap; az aktív
 * menüpont alatt 2 px-es világos csík; 900 px alatt ☰ menü). Csak belépve látszik: kilépve az oldal maga a belépő lap.
 *
 * Balra a márka és a fő részek (Dashboard · Docs · Changelog), jobbra a stáb lapja (admin, owner) és a kilépés.
 * A Dashboard fülei (licenc, route-ok, könyvtár, review, farm) a lapon belül vannak.
 */

const MAIN_LINKS = [
  { to: '/licence', label: 'Dashboard', match: p => /^\/(licence|routes|library|review|farming)(\/|$)/.test(p) },
  { to: '/docs', label: 'Docs', match: p => p.startsWith('/docs') },
  { to: '/changelog', label: 'Changelog', match: p => p.startsWith('/changelog') },
];

function navCls(active) {
  return `relative inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors duration-150
          ${active
            ? 'text-gray-50 after:absolute after:left-2.5 after:right-2.5 after:-bottom-[13px] after:h-0.5 after:rounded after:bg-gray-50'
            : 'text-gray-500 hover:text-gray-100 hover:bg-white/[0.04]'}`;
}

function SignOutIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-[15px] h-[15px]" fill="none" stroke="currentColor" strokeWidth="1.75"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" /><path d="M9.5 8l-4 4 4 4" /><path d="M5.5 12H15" />
    </svg>
  );
}

export default function BotNavbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  // Útvonalváltáskor a mobil menü magától záródjon.
  useEffect(() => { setOpen(false); }, [pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const staff = isStaffRole(user?.role);

  return (
    <header className="sticky top-0 z-50 bg-gray-950/[0.72] backdrop-blur-[14px] backdrop-saturate-[1.4] border-b border-line-faint">
      <div className="wrap h-[60px] flex items-center gap-7">
        <Link to="/licence" className="flex items-center gap-2.5 shrink-0" aria-label="MortisEngine dashboard">
          <BrandMark />
        </Link>

        <nav aria-label="Main" className="hidden min-[900px]:flex items-center gap-0.5">
          {MAIN_LINKS.map(l => (
            <Link key={l.to} to={l.to} className={navCls(l.match(pathname))}
                  aria-current={l.match(pathname) ? 'page' : undefined}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {staff && (
            <Link to="/staff" className={`hidden sm:inline-flex ${navCls(pathname.startsWith('/staff'))}`}
                  aria-current={pathname.startsWith('/staff') ? 'page' : undefined}>
              Staff
            </Link>
          )}
          <span className="hidden sm:inline font-mono text-[12px] text-gray-600 px-1 max-w-[160px] truncate" title={user?.username}>
            {user?.username}
          </span>
          <button type="button" onClick={handleLogout} className={`hidden sm:inline-flex ${navCls(false)}`}>
            <SignOutIcon />Sign out
          </button>

          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            className="min-[900px]:hidden inline-grid place-items-center w-10 h-10 rounded-lg bg-gray-800 border border-line
                       text-gray-400 hover:text-gray-50 hover:bg-gray-700 transition-colors"
          >
            <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.75"
                 strokeLinecap="round" viewBox="0 0 24 24" aria-hidden="true">
              {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobil menü */}
      {open && (
        <nav aria-label="Main" className="min-[900px]:hidden border-t border-line-faint bg-gray-950 animate-fade-in">
          <div className="wrap py-3 flex flex-col gap-0.5">
            {MAIN_LINKS.map(l => (
              <Link key={l.to} to={l.to}
                    className={`h-11 px-2.5 flex items-center rounded-lg text-[15px] font-medium ${
                      l.match(pathname) ? 'bg-gray-850 text-gray-50' : 'text-gray-400 hover:text-gray-50 hover:bg-white/[0.04]'
                    }`}>
                {l.label}
              </Link>
            ))}
            <div className="mt-2 pt-3 border-t border-line-faint flex flex-col gap-0.5">
              {staff && (
                <Link to="/staff" className="h-11 px-2.5 flex items-center rounded-lg text-[15px] text-gray-400 hover:text-gray-50">
                  Staff
                </Link>
              )}
              <button type="button" onClick={handleLogout}
                      className="h-11 px-2.5 flex items-center gap-2 rounded-lg text-[15px] text-gray-400 hover:text-gray-50 text-left">
                <SignOutIcon />Sign out <span className="ml-auto font-mono text-[12px] text-gray-600">{user?.username}</span>
              </button>
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}
